import { View, Text, TextInput } from 'react-native';
import { useState, useEffect } from 'react';
import { usePathname, useRouter } from 'expo-router';
import { trpc } from '../../lib/trpc';
import { trackOnboardingStepCompleted } from '../../lib/product-events';
import {
  OnboardingShell,
  Button,
  Card,
  WebHead,
} from '../../components';

interface DependentRow {
  key: string;
  dbId?: string; // set if already persisted
  firstName: string;
  birthYear: string;
  k12Type: 'public' | 'private';
  collegeType: 'state' | 'private' | 'none';
}

function newRow(): DependentRow {
  return { key: String(Date.now() + Math.random()), firstName: '', birthYear: '', k12Type: 'public', collegeType: 'state' };
}

export default function Step6Family() {
  const router = useRouter();
  const pathname = usePathname();

  const profilesQ = trpc.profiles.list.useQuery();
  const dependentsQ = trpc.dependents.list.useQuery();
  const createDependentMutation = trpc.dependents.create.useMutation();
  const deleteDependentMutation = trpc.dependents.delete.useMutation();
  const setEducationPrefsMutation = trpc.dependents.setEducationPrefs.useMutation();
  const createPartnerMutation = trpc.profiles.create.useMutation();
  const advanceMutation = trpc.households.updateOnboardingStep.useMutation();
  const updateProfileMutation = trpc.profiles.update.useMutation();

  const primaryProfile = profilesQ.data?.find((p) => p.type === 'primary') ?? profilesQ.data?.[0];
  const existingSpouse = profilesQ.data?.find((p) => p.type === 'spouse') ?? null;
  const hasSpouse = !!existingSpouse;

  // Partner
  const [addingPartner, setAddingPartner] = useState(false);
  const [partnerFirstName, setPartnerFirstName] = useState('');
  const [partnerBirthYear, setPartnerBirthYear] = useState('');

  // Children rows — hydrate from DB on first load
  const [rows, setRows] = useState<DependentRow[]>([]);
  const [hydratedDeps, setHydratedDeps] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (hydratedDeps || !dependentsQ.data) return;
    if (dependentsQ.data.length > 0) {
      setRows(dependentsQ.data.map((d) => ({
        key: d.id,
        dbId: d.id,
        firstName: d.firstName,
        birthYear: String(d.birthYear),
        k12Type: (d.educationPrefs?.k12Type as 'public' | 'private') ?? 'public',
        collegeType: (d.educationPrefs?.collegeType as 'state' | 'private' | 'none') ?? 'state',
      })));
    }
    setHydratedDeps(true);
  }, [dependentsQ.data, hydratedDeps]);

  const addRow = () => setRows((prev) => [...prev, newRow()]);

  const updateRow = (key: string, field: keyof DependentRow, value: string) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
    setError(null);
  };

  const removeRow = (key: string) => setRows((prev) => prev.filter((r) => r.key !== key));

  const handleContinue = async () => {
    setError(null);

    if (addingPartner && !hasSpouse) {
      if (!partnerFirstName.trim()) {
        setError("Please enter your partner's first name.");
        return;
      }
      const pYear = parseInt(partnerBirthYear);
      if (!pYear || pYear < 1920 || pYear > 2010) {
        setError("Please enter a valid birth year for your partner.");
        return;
      }
    }

    for (const row of rows) {
      const hasName = row.firstName.trim().length > 0;
      const hasYear = row.birthYear.trim().length > 0;
      if (!hasName && !hasYear) continue;
      if (!hasName) { setError('Please enter a name for each child.'); return; }
      const year = parseInt(row.birthYear);
      if (!year || year < 1990 || year > new Date().getFullYear()) {
        setError('Please enter a valid birth year (1990–present) for each child.');
        return;
      }
    }

    const filledRows = rows.filter((r) => r.firstName.trim() && parseInt(r.birthYear) > 0);

    try {
      if (addingPartner && !hasSpouse) {
        await createPartnerMutation.mutateAsync({
          firstName: partnerFirstName.trim(),
          birthYear: parseInt(partnerBirthYear),
          lifeExpectancy: 90,
        });
        if (primaryProfile) {
          await updateProfileMutation.mutateAsync({
            id: primaryProfile.id,
            filingStatus: 'married_filing_jointly',
          });
        }
      }

      // Delete dependents that were removed from the list
      const currentDbIds = new Set(rows.map((r) => r.dbId).filter(Boolean));
      for (const dep of dependentsQ.data ?? []) {
        if (!currentDbIds.has(dep.id)) {
          await deleteDependentMutation.mutateAsync({ id: dep.id });
        }
      }

      for (const row of filledRows) {
        let dependentId = row.dbId;
        if (!dependentId) {
          const created = await createDependentMutation.mutateAsync({
            firstName: row.firstName.trim(),
            birthYear: parseInt(row.birthYear),
            relationship: 'child',
          });
          dependentId = created.id;
        }
        if (row.k12Type !== 'public' || row.collegeType !== 'state') {
          await setEducationPrefsMutation.mutateAsync({
            dependentId,
            k12Type: row.k12Type,
            collegeType: row.collegeType,
          });
        }
      }

      await advanceMutation.mutateAsync({ step: 7 });
      trackOnboardingStepCompleted({
        route: pathname,
        stepNumber: 6,
        nextStepNumber: 7,
        completionKind: 'continue',
        metadata: {
          childCount: filledRows.length,
          hasPartner: addingPartner || hasSpouse,
        },
      });
      router.replace('/(onboarding)/step-7-life-events' as any);
    } catch {
      setError('Could not save your family info. Please try again.');
    }
  };

  const isSaving =
    createDependentMutation.isPending ||
    deleteDependentMutation.isPending ||
    setEducationPrefsMutation.isPending ||
    createPartnerMutation.isPending ||
    advanceMutation.isPending ||
    updateProfileMutation.isPending;

  return (
    <OnboardingShell
      step={5}
      title="Your Household"
      subtitle="Add a partner or children so Per Diem can suggest household milestones and plan around shared goals."
      footer={
        <View className="gap-3">
          {error && (
            <View className="rounded-xl bg-red-50 px-4 py-3">
              <Text className="text-sm text-red-600">{error}</Text>
            </View>
          )}
          <Button
            testID="onboarding-family-continue-button"
            accessibilityLabel="Continue from household review"
            onPress={handleContinue}
            loading={isSaving}
          >
            Continue →
          </Button>
        </View>
      }
    >
      <WebHead><title>Step 6: Your Household – Per Diem</title></WebHead>
      <View className="gap-4">

        {/* Partner */}
        <Card>
          <Text className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
            Partner
          </Text>
          {hasSpouse ? (
            <Text className="text-xs text-gray-400">
              Partner already added — you can manage them in Settings.
            </Text>
          ) : addingPartner ? (
            <View className="gap-3 mt-2">
              <View className="flex-row gap-2">
                <TextInput
                  value={partnerFirstName}
                  onChangeText={(v) => { setPartnerFirstName(v); setError(null); }}
                  placeholder="Partner's first name"
                  autoCapitalize="words"
                  autoCorrect={false}
                  className="flex-1 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-sm text-gray-800 dark:text-gray-200"
                />
                <TextInput
                  value={partnerBirthYear}
                  onChangeText={(v) => { setPartnerBirthYear(v); setError(null); }}
                  placeholder="Birth year"
                  keyboardType="numeric"
                  className="w-28 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-sm text-gray-800 dark:text-gray-200"
                />
              </View>
              <Button variant="ghost" size="sm" onPress={() => { setAddingPartner(false); setPartnerFirstName(''); setPartnerBirthYear(''); }}>
                Remove
              </Button>
            </View>
          ) : (
            <Button variant="secondary" size="sm" className="mt-2" onPress={() => setAddingPartner(true)}>
              + Add a partner
            </Button>
          )}
        </Card>

        {/* Children */}
        <Card>
          <Text className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
            Children & Other Dependents
          </Text>
          <Text className="text-xs text-gray-400 mb-4">
            Per Diem uses birth years to suggest education costs, health insurance timelines, and more.
          </Text>
          {rows.length > 0 && (
            <View className="mb-3">
              {rows.map((row) => (
                <View key={row.key} className={"gap-2 py-3 border-b border-gray-100 dark:border-gray-700"}>
                  <View className="flex-row gap-2 items-center">
                    <TextInput
                      value={row.firstName}
                      onChangeText={(v) => updateRow(row.key, 'firstName', v)}
                      placeholder="First name"
                      autoCapitalize="words"
                      autoCorrect={false}
                      className="flex-1 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-sm text-gray-800 dark:text-gray-200"
                    />
                    <TextInput
                      value={row.birthYear}
                      onChangeText={(v) => updateRow(row.key, 'birthYear', v)}
                      placeholder="Birth year"
                      keyboardType="numeric"
                      className="w-28 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-sm text-gray-800 dark:text-gray-200"
                    />
                    <Button variant="ghost" size="sm" onPress={() => removeRow(row.key)}>✕</Button>
                  </View>
                  <View className="gap-2 pl-1">
                    <View className="flex-row items-center gap-2">
                      <Text className="text-xs text-gray-500 dark:text-gray-400 w-14">K-12</Text>
                      <View className="flex-1 flex-row gap-1">
                        {(['public', 'private'] as const).map((v) => (
                          <Button
                            key={v}
                            size="sm"
                            variant={row.k12Type === v ? 'primary' : 'secondary'}
                            className="flex-1"
                            onPress={() => updateRow(row.key, 'k12Type', v)}
                          >
                            {v.charAt(0).toUpperCase() + v.slice(1)}
                          </Button>
                        ))}
                      </View>
                    </View>
                    <View className="flex-row items-center gap-2">
                      <Text className="text-xs text-gray-500 dark:text-gray-400 w-14">College</Text>
                      <View className="flex-1 flex-row gap-1">
                        {(['state', 'private', 'none'] as const).map((v) => (
                          <Button
                            key={v}
                            size="sm"
                            variant={row.collegeType === v ? 'primary' : 'secondary'}
                            className="flex-1"
                            onPress={() => updateRow(row.key, 'collegeType', v)}
                          >
                            {v === 'state' ? 'State' : v === 'private' ? 'Private' : 'None'}
                          </Button>
                        ))}
                      </View>
                    </View>
                  </View>
                </View>
              ))}
            </View>
          )}
          <Button variant="secondary" size="sm" onPress={addRow}>
            + Add a child
          </Button>
        </Card>

      </View>
    </OnboardingShell>
  );
}
